import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import KanonicsLogo from "./KanonicsLogo";
import DotGrid from "./DotGrid";

interface Props {
  isVisible: boolean;
}

const LINE_COLOR = "rgba(29,47,65,0.15)";
const GAP_PX = 30;
const LINE_GAP = 50;
const LOGO_GAP = 14;
const FADE_MS = 120; // background fades fast so blobs appear quickly
const DRIFT_DELAY_MS = 0; // drift starts immediately
const EXIT_MS = DRIFT_DELAY_MS + 1900;

const LoadingScreen: React.FC<Props> = ({ isVisible }) => {
  const [dims, setDims] = useState({
    w: window.innerWidth,
    h: window.innerHeight,
  });
  const [showGrid, setShowGrid] = useState(isVisible);
  const impulseRef = useRef<number | null>(null);
  const exitRef = useRef<number | null>(null);

  useEffect(() => {
    const update = () =>
      setDims({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  useEffect(() => {
    if (!isVisible) {
      exitRef.current = performance.now();
      const removeTimer = setTimeout(() => setShowGrid(false), EXIT_MS);
      return () => clearTimeout(removeTimer);
    }
  }, [isVisible]);

  const cols = Math.ceil(dims.w / GAP_PX) + 1;
  const rows = Math.ceil(dims.h / GAP_PX) + 1;
  const isMobile = dims.w < 600;
  const vPos = isMobile ? "15%" : "28%";
  const hPos = "78%";
  const logoH = isMobile ? 20 : 34;

  return (
    <>
      {showGrid && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 9998,
            pointerEvents: "none",
            overflow: "hidden",
          }}
        >
          <DotGrid
            impulseRef={impulseRef}
            exitRef={exitRef}
            cols={cols}
            rows={rows}
            gap={GAP_PX}
            fullOpacity
          />
        </div>
      )}

      <AnimatePresence>
        {isVisible && (
          <motion.div
            key="loading-screen"
            initial={{ opacity: 1 }}
            exit={{
              opacity: 0,
              transition: { duration: FADE_MS / 1000, ease: "easeIn" },
            }}
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 9999,
              background: "#f8f9fb",
              overflow: "hidden",
              willChange: "opacity",
              WebkitBackfaceVisibility: "hidden",
              backfaceVisibility: "hidden",
              pointerEvents: "none",
            }}
          >
            {/* Vertical line */}
            <motion.div
              initial={{ scaleY: 0, opacity: 1 }}
              animate={{ scaleY: 1, opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1] }}
              style={{
                position: "absolute",
                left: vPos,
                top: 0,
                width: 1,
                height: "100%",
                background: LINE_COLOR,
                transformOrigin: "top center",
                willChange: "transform, opacity",
              }}
            />

            <motion.div
              initial={{ scaleX: 0, opacity: 1 }}
              animate={{ scaleX: 1, opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.75, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
              style={{
                position: "absolute",
                top: hPos,
                left: 0,
                width: `calc(${vPos} - ${LINE_GAP}px)`,
                height: 1,
                background: LINE_COLOR,
                transformOrigin: "right center",
                willChange: "transform, opacity",
              }}
            />

            <motion.div
              initial={{ scaleX: 0, opacity: 1 }}
              animate={{ scaleX: 1, opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.75, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
              style={{
                position: "absolute",
                top: hPos,
                left: `calc(${vPos} + ${LINE_GAP}px)`,
                right: 0,
                height: 1,
                background: LINE_COLOR,
                transformOrigin: "left center",
                willChange: "transform, opacity",
              }}
            />

            {/* Logo */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.65, delay: 0.72, ease: [0.22, 1, 0.36, 1] }}
              style={{
                position: "absolute",
                top: `calc(${hPos} - ${logoH + LOGO_GAP}px)`,
                right: "8%",
              }}
            >
              <KanonicsLogo height={logoH} color="#B0B0B1" />
            </motion.div>

            {/* Gold progress bar */}
            <motion.div
              initial={{ scaleX: 0, opacity: 1 }}
              animate={{ scaleX: 1, opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 1.8, ease: [0.4, 0, 0.2, 1] }}
              style={{
                position: "absolute",
                bottom: 0,
                left: 0,
                width: "100%",
                height: 2,
                background: "#D1BE92",
                willChange: "transform, opacity",
                transformOrigin: "left center",
              }}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default LoadingScreen;
